import { useCallback, useEffect } from "react";
import { useQueryClient, useQuery, QueryFilters } from "@tanstack/react-query";

import qs from "query-string";

import mergeWith from "lodash/mergeWith";
import filter from "lodash/filter";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";

import { useAppContext } from "..";
import { useSchedulesContext } from "../../schedules";

const useBoardStatus = () => {
  const queryClient = useQueryClient();

  const { screenId, clientId, config } = useAppContext();

  const { scheduleScreensaver } = useSchedulesContext();

  const onMessage = useCallback(async (event: MessageEvent<any>) => {
    const { data: rawMessage } = event;

    try {
      const message: ScreenMessage = JSON.parse(rawMessage);

      if (!(message && message.action)) {
        return;
      }

      const predicate: QueryFilters["predicate"] = ({ queryKey }) => {
        if (!(Array.isArray(queryKey) && queryKey.length)) {
          return false;
        }

        const [key, params] = queryKey;

        if (key !== "digitalReception") {
          return false;
        }

        return "id" in params && params.id === screenId;
      };

      switch (message.action) {
        case "restart": {
          window.location.href = qs.stringifyUrl({
            url: `${window.location.origin}/client/${clientId}/board/${screenId}`,
            query: config,
          });

          break;
        }

        case "refresh": {
          const { digitalReception } = await queryClient.fetchQuery<{
            digitalReception: IDigitalReception;
          }>(["digitalReception", { id: screenId }]);

          if (config.preview) {
            break;
          }

          const sleepSchedules = filter(
            digitalReception?.schedules,
            (item) => item.type === "sleep" && !item.schedule.disabled,
          );

          if (sleepSchedules.length > 0) {
            scheduleScreensaver(sleepSchedules);
          }

          break;
        }

        case "update-digitalReception": {
          const { data } = message;

          queryClient.setQueriesData<{ digitalReception: IDigitalReception }>(
            {
              predicate,
            },
            (prev) => {
              // prettier-ignore
              if (!(prev && prev.digitalReception && data && data.digitalReception)) {
                return data;
              }

              const { digitalReception: prevDigitalReception, ...rest } = prev;
              const { digitalReception: partialDigitalReception } = data as {
                digitalReception: Partial<IDigitalReception>;
              };

              // eslint-disable-next-line consistent-return
              const digitalReception = mergeWith(
                prevDigitalReception,
                partialDigitalReception,
                (obj, src) => {
                  if (isArray(obj)) {
                    return src;
                  }

                  if (isObject(obj)) {
                    return mergeWith(obj, src);
                  }

                  return src;
                },
              );

              return {
                ...rest,
                digitalReception,
              };
            },
          );

          break;
        }

        default: {
          break;
        }
      }
    } catch {
      //
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => window.removeEventListener("message", onMessage);
  }, []);

  return useQuery(
    [`network/board/${screenId}`],
    ({ queryKey: [url] }) => {
      return new Promise((resolve, reject) => {
        const baseURL = process.env.API_URL.replace(
          /(http)(s)?\:\/\//,
          "ws$2://",
        );

        const socket = new WebSocket(`${baseURL}/${url}`);

        if (socket.readyState === WebSocket.OPEN) {
          resolve(true);

          return;
        }

        socket.onopen = function (e) {
          return resolve(e);
        };

        socket.onerror = function (error) {
          queryClient.invalidateQueries([url], { exact: true });

          return reject(error);
        };

        socket.onmessage = function (event) {
          onMessage(event);
        };

        socket.onclose = () => {
          queryClient.invalidateQueries([url], { exact: true });

          return reject(new Error("Socket close. Retry"));
        };
      });
    },
    {
      retry: true,
      retryDelay: 5000,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      enabled: config?.connect,
    },
  );
};

export default useBoardStatus;
